/**
 * Unified Layer System
 * 
 * A single module that handles layer creation, composition, and event sourcing.
 * No circular dependencies, everything in one place.
 */

import { VoxelMap, VoxelData, type Face } from '../voxelTypes'
import { Position, parseKeyX, parseKeyY, parseKeyZ, posKeyXYZ } from '@/voxelMath'
import { recordMutationCandidate } from '@/events/mutationJournal'
import { canonicalVoxelType } from '@/voxels/registry'
// No position helpers needed here

// ============================================================================
// Blend Modes
// ============================================================================

export type BlendMode =
  | 'place'    // Add B, replace A where overlapping
  | 'fill'     // Add B, keep A where overlapping  
  | 'subtract' // Remove B's positions from A
  | 'mask'     // Keep A where B exists
  | 'xor'      // Keep non-overlapping from A and B
  | 'paint'    // Apply B's properties to A's voxels


/**
 * Compose two voxel layers using blend mode
 */
export function blend(a: VoxelMap, b: VoxelMap | ReadonlyArray<number>, mode: BlendMode): VoxelMap {
  const result = new Map<number, VoxelData>()
  const isMapLike = (m: any): boolean => !!m && typeof m.size === 'number' && typeof m.forEach === 'function'
  switch (mode) {
    case 'place':
      // Start with A, then add all of B (B wins overlaps)
      for (const [key, voxel] of a) result.set(key, voxel)
      if (isMapLike(b)) { for (const [key, voxel] of (b as any)) result.set(key, voxel) }
      break

    case 'fill':
      // Start with A, add B only where A doesn't exist
      for (const [key, voxel] of a) result.set(key, voxel)
      if (isMapLike(b)) { for (const [key, voxel] of (b as any)) if (!result.has(key)) result.set(key, voxel) }
      break

    case 'subtract':
      if (Array.isArray(b)) {
        const remove = new Set(b)
        for (const [key, voxel] of a) if (!remove.has(key)) result.set(key, voxel)
      } else if (isMapLike(b)) {
        for (const [key, voxel] of a) if (!(b as any).has(key)) result.set(key, voxel)
      }
      break

    case 'mask':
      // Keep A only where B exists
      if (isMapLike(b)) { for (const [key, voxel] of a) if ((b as any).has(key)) result.set(key, voxel) }
      break

    case 'xor':
      // Keep non-overlapping from both
      if (isMapLike(b)) {
        for (const [key, voxel] of a) if (!(b as any).has(key)) result.set(key, voxel)
        for (const [key, voxel] of (b as any)) if (!(a as any).has(key)) result.set(key, voxel)
      }
      break

    case 'paint':
      // Apply B's properties to A's voxels (only where both exist)
      if (isMapLike(b)) {
        for (const [key, voxel] of a) {
          if ((b as any).has(key)) result.set(key, { ...voxel, ...((b as any).get(key) as VoxelData) })
          else result.set(key, voxel)
        }
      }
      break
  }
  return result
}

/**
 * Compose two voxel layers using blend mode, mutating 'a' instead of creating new map
 */
export function blendMutable(
  a: VoxelMap,
  b: VoxelMap | ReadonlyArray<number>,
  mode: BlendMode,
  mirrorCtx?: { bounds: Position; mirrors: MirrorSpec } | null
): VoxelMap {
  const isMapLike = (m: any): boolean => !!m && typeof m.size === 'number' && typeof m.forEach === 'function'

  // Optional mirroring: build a mirrored payload once, then proceed with original logic
  if (mirrorCtx && hasMirrors(mirrorCtx.mirrors)) {
    const { bounds, mirrors } = mirrorCtx
    if (mode === 'subtract') {
      if (Array.isArray(b)) {
        const out: number[] = []
        for (const key of b) {
          out.push(key)
          forEachReflection(key, bounds, mirrors, (_mx, _my, _mz, kRef) => out.push(kRef))
        }
        b = out
      } else if (isMapLike(b)) {
        const out = new Map<number, VoxelData>()
        ;(b as VoxelMap).forEach((v, k) => {
          out.set(k, v)
          forEachReflection(k, bounds, mirrors, (_mx, _my, _mz, kRef) => out.set(kRef, v))
        })
        b = out
      }
    } else {
      // place/fill/mask/xor/paint expect Map-like B
      if (isMapLike(b)) {
        const out = new Map<number, VoxelData>()
        ;(b as VoxelMap).forEach((v, k) => {
          // Base
          out.set(k, v)
          // Reflections (flip face for oriented voxels)
          const face = (v as any).face as Face | undefined
          forEachReflection(k, bounds, mirrors, (mx, my, mz, kRef) => {
            if (face) out.set(kRef, { ...v, face: flipFace(face, mx, my, mz) })
            else out.set(kRef, v)
          })
        })
        b = out
      }
      // If b is an array here (unexpected), promote to Map with empty voxels
      else if (Array.isArray(b)) {
        const out = new Map<number, VoxelData>()
        for (const k of b) {
          out.set(k, {} as VoxelData)
          forEachReflection(k, bounds, mirrors, (_mx, _my, _mz, kRef) => out.set(kRef, {} as VoxelData))
        }
        b = out
      }
    }
  }
  switch (mode) {
    case 'place':
      // Add all of B to A (B wins overlaps)
      if (isMapLike(b)) { for (const [key, voxel] of (b as any)) writePlace(a, key, voxel) }
      break

    case 'fill':
      // Add B only where A doesn't exist
      if (isMapLike(b)) { for (const [key, voxel] of (b as any)) writeFill(a, key, voxel) }
      break

    case 'subtract':
      // Remove positions that exist in B
      if (Array.isArray(b)) { for (const key of b) writeSubtract(a, key) }
      else { (b as any).forEach((_: any, key: number) => writeSubtract(a, key)) }
      break

    case 'mask':
      // Keep A only where B exists
      const keysToDelete = new Set<number>()
      ;(a as any).forEach((_: any, key: number) => {
        if (!(b as any).has(key)) {
          keysToDelete.add(key)
        }
      })
      keysToDelete.forEach(key => writeSubtract(a, key))
      break

    case 'xor':
      // Remove overlapping, add non-overlapping from B
      const overlapping = new Set<number>()
      if (isMapLike(b)) { for (const key of (a as any).keys()) if ((b as any).has(key)) overlapping.add(key) }
      overlapping.forEach(key => writeSubtract(a, key))
      if (isMapLike(b)) { for (const [key, voxel] of (b as any)) if (!overlapping.has(key)) writePlace(a, key, voxel) }
      break

    case 'paint':
      // Apply B's properties to A's voxels (only where both exist)
      if (isMapLike(b)) {
        for (const [key, voxel] of a as any) {
          if ((b as any).has(key)) writePlace(a, key, { ...voxel, ...((b as any).get(key) as VoxelData) })
        }
      }
      break
  }
  return a
}

// ============================================================================
// Direct Writers (reusable helpers)
// ============================================================================

export function writePlace(target: VoxelMap, key: number, voxel: VoxelData) {
  recordMutationCandidate(key)
  target.set(key, voxel)
}

export function writeFill(target: VoxelMap, key: number, voxel: VoxelData) {
  if (!target.has(key)) {
    recordMutationCandidate(key)
    target.set(key, voxel)
  }
}

export function writeSubtract(target: VoxelMap, key: number, _voxel?: VoxelData) {
  recordMutationCandidate(key)
  target.delete(key)
}

// ============================================================================
// Scoped writers (scoped dig and smear, materials-perf-plan B5)
// ============================================================================

/**
 * A write scope: "this material" is the canonical voxel type (`canonicalVoxelType`), so colour,
 * material finish and face do not matter. Digging a two-colour sand pile removes all of the sand and
 * none of the castle; smearing clay recolours every clay cell under the brush. Each scoped rule is
 * an idempotent per-cell rule, so brush folds over them stay split-invariant.
 */
export type WriteScope = { onlyType: string /* canonical */ }

export function isInWriteScope(existing: VoxelData | undefined, scope: WriteScope): boolean {
  return !!existing && canonicalVoxelType(existing.type) === scope.onlyType
}

/** Deletes `key` only when it holds a voxel of the scope's canonical type. */
export function writeSubtractIf(target: VoxelMap, key: number, scope: WriteScope) {
  if (!isInWriteScope(target.get(key), scope)) return
  recordMutationCandidate(key)
  target.delete(key)
}

/**
 * Protected types (an eraser's `protect` list, `BrushSnapshot.keep`): canonical voxel types an erase
 * never removes. Like a scope, "this type" is the canonical type, so colour and finish do not matter.
 */
export type KeepTypes = ReadonlySet<string>

export function isKept(existing: VoxelData | undefined, keep: KeepTypes): boolean {
  return !!existing && keep.has(canonicalVoxelType(existing.type))
}

/**
 * Deletes `key` unless it holds a protected type; with a scope, also only when it holds the scope's
 * type. Idempotent per cell (it only ever deletes), so brush folds over it stay split-invariant.
 */
export function writeSubtractKeeping(target: VoxelMap, key: number, keep: KeepTypes, scope?: WriteScope) {
  const existing = target.get(key)
  if (!existing || isKept(existing, keep)) return
  if (scope && !isInWriteScope(existing, scope)) return
  recordMutationCandidate(key)
  target.delete(key)
}

/** Rewrites `key` to `voxel` only when it holds a voxel of the scope's canonical type. */
export function writeRepaintIf(target: VoxelMap, key: number, voxel: VoxelData, scope: WriteScope) {
  const existing = target.get(key)
  if (!isInWriteScope(existing, scope) || existing === voxel) return
  recordMutationCandidate(key)
  target.set(key, voxel)
}

// ============================================================================
// Mirror Helpers (shared by writeFn and future bulk paths)
// ============================================================================

export type MirrorSpec = { x?: number; y?: number; z?: number }

function hasMirrors(m?: MirrorSpec | null): boolean {
  if (!m) return false
  return Number.isFinite(m.x as number) || Number.isFinite(m.y as number) || Number.isFinite(m.z as number)
}

// Batch-scoped active mirror context (set by reducer)
let ACTIVE_MIRROR_CTX: { bounds: Position; mirrors: MirrorSpec } | null = null
export function setActiveMirrorCtx(ctx: { bounds: Position; mirrors: MirrorSpec } | null) {
  // Normalize: treat empty/invalid mirrors as null to keep fast-paths
  ACTIVE_MIRROR_CTX = (ctx && hasMirrors(ctx.mirrors)) ? ctx : null
}

function flipFace(face: Face | undefined, maskX: boolean, maskY: boolean, maskZ: boolean): Face | undefined {
  if (!face) return face
  let f = face
  const swap = (inFace: Face, aPos: Face, aNeg: Face, active: boolean): Face => {
    if (!active) return inFace
    return inFace === aPos ? aNeg : (inFace === aNeg ? aPos : inFace)
  }
  f = swap(f, 'x+', 'x-', maskX)
  f = swap(f, 'y+', 'y-', maskY)
  f = swap(f, 'z+', 'z-', maskZ)
  return f
}

// Enumerate reflected variants for a single key based on active mirrors.
// Emits up to 7 reflections (one per non-empty subset of active axes). The caller should
// perform the original write separately for zero-overhead when no mirroring is needed.
function forEachReflection(
  key: number,
  bounds: Position,
  mirrors: MirrorSpec,
  emit: (maskX: boolean, maskY: boolean, maskZ: boolean, reflectedKey: number) => void
) {
  const hasX = Number.isFinite(mirrors.x as number)
  const hasY = Number.isFinite(mirrors.y as number)
  const hasZ = Number.isFinite(mirrors.z as number)
  if (!hasX && !hasY && !hasZ) return

  const x0 = parseKeyX(key)
  const y0 = parseKeyY(key)
  const z0 = parseKeyZ(key)
  // Reflect across edge-planes at half indices: R_half = e + 0.5
  // Integer-safe form: x' = (2*e + 1) - x
  const twoExPlus1 = hasX ? ((mirrors.x as number) * 2 + 1) : 0
  const twoEyPlus1 = hasY ? ((mirrors.y as number) * 2 + 1) : 0
  const twoEzPlus1 = hasZ ? ((mirrors.z as number) * 2 + 1) : 0

  // Build subset masks of active axes (exclude identity)
  const masks: Array<[boolean, boolean, boolean]> = []
  if (hasX) masks.push([true, false, false])
  if (hasY) masks.push([false, true, false])
  if (hasZ) masks.push([false, false, true])
  if (hasX && hasY) masks.push([true, true, false])
  if (hasX && hasZ) masks.push([true, false, true])
  if (hasY && hasZ) masks.push([false, true, true])
  if (hasX && hasY && hasZ) masks.push([true, true, true])

  for (const [mx, my, mz] of masks) {
    let nx = x0, ny = y0, nz = z0
    if (mx) nx = twoExPlus1 - x0
    if (my) ny = twoEyPlus1 - y0
    if (mz) nz = twoEzPlus1 - z0
    // Bounds check
    if (nx < 0 || ny < 0 || nz < 0 || nx >= bounds.x || ny >= bounds.y || nz >= bounds.z) continue
    emit(mx, my, mz, posKeyXYZ(nx, ny, nz))
  }
}

/**
 * Get the appropriate writer function for a blend mode
 */
export function writeFn(
  mode: BlendMode,
  target: VoxelMap,
  mirrorCtx?: { bounds: Position; mirrors: MirrorSpec } | null,
  /** Scoped dig (B5): a `subtract` writer with a scope only deletes cells of that canonical type. */
  scope?: WriteScope | null,
) {
  // Resolve context from arg or active batch context
  const resolved = mirrorCtx ?? ACTIVE_MIRROR_CTX
  const subtractAt = scope && mode === 'subtract'
    ? (key: number) => writeSubtractIf(target, key, scope)
    : (key: number) => writeSubtract(target, key)
  // Fast path: no mirroring — return minimal writer without any branching inside
  if (!resolved || !hasMirrors(resolved.mirrors)) {
    switch (mode) {
      case 'place':
        return (key: number, voxel: VoxelData) => writePlace(target, key, voxel)
      case 'fill':
        return (key: number, voxel: VoxelData) => writeFill(target, key, voxel)
      case 'subtract':
        return (key: number, _voxel: VoxelData) => subtractAt(key)
      default:
        // For modes that don't have simple writers (mask, xor, paint), default to place
        return (key: number, voxel: VoxelData) => writePlace(target, key, voxel)
    }
  }

  const { bounds, mirrors } = resolved

  if (mode === 'subtract') {
    return (key: number, _voxel: VoxelData) => {
      // Base
      subtractAt(key)
      // Reflections
      forEachReflection(key, bounds, mirrors, (_mx, _my, _mz, kRef) => subtractAt(kRef))
    }
  }

  // place/fill use similar logic; mask/xor/paint default to place semantics when used as a writer
  const doWrite = (k: number, v: VoxelData) => {
    if (mode === 'fill') writeFill(target, k, v)
    else writePlace(target, k, v)
  }

  return (key: number, voxel: VoxelData) => {
    // Base write
    doWrite(key, voxel)
    // Reflections
    forEachReflection(key, bounds, mirrors, (mx, my, mz, kRef) => {
      const hasFace = (voxel as any).face as Face | undefined
      if (hasFace) {
        const f = flipFace(hasFace, mx, my, mz)
        doWrite(kRef, { ...voxel, face: f })
      } else {
        doWrite(kRef, voxel)
      }
    })
  }
}

// ============================================================================
// Transform Types
// ============================================================================

type Int = number  // integers only
type Vec3i = { x: Int; y: Int; z: Int }

export type IntTRS = {
  t: Vec3i  // translation
  r?: { x: 0 | 1 | 2 | 3; y: 0 | 1 | 2 | 3; z: 0 | 1 | 2 | 3 }  // 90° turns
  s?: Vec3i  // scale
  pivot?: Position  // transform origin
}
